import { create } from "zustand";
import { clearAllSubscriptions } from "./watchlistQuoteStore";

export const DEFAULT_COLUMN_CONFIG = [
  {
    key: "direction",
    label: "Direção",
    visible: true,
    baseWidth: 4,
    fixed: true,
  },
  { key: "lastPrice", label: "Último", visible: true, baseWidth: 13 },
  { key: "change", label: "Variação", visible: true, baseWidth: 13 },
  { key: "changePct", label: "Var %", visible: true, baseWidth: 11 },
  { key: "high", label: "Máx", visible: true, baseWidth: 11 },
  { key: "low", label: "Mín", visible: true, baseWidth: 11 },
  { key: "volume", label: "Volume", visible: true, baseWidth: 11 },
  { key: "time", label: "Horário", visible: true, baseWidth: 11 },
];

const useWatchlistStore = create((set, get) => ({
  watchlists: { Favoritos: [] },
  columnConfigs: {},
  activeWatchlist: "Favoritos",
  group: null, // Linking group: null = no group, or 1-5

  addSymbol: (symbol) =>
    set((state) => {
      const list = state.watchlists[state.activeWatchlist] || [];
      if (list.includes(symbol)) return state;
      return {
        watchlists: {
          ...state.watchlists,
          [state.activeWatchlist]: [...list, symbol],
        },
      };
    }),

  removeSymbol: (symbol) =>
    set((state) => ({
      watchlists: {
        ...state.watchlists,
        [state.activeWatchlist]: (
          state.watchlists[state.activeWatchlist] || []
        ).filter((s) => s !== symbol),
      },
    })),

  reorderSymbols: (fromIdx, toIdx) =>
    set((state) => {
      const list = [...(state.watchlists[state.activeWatchlist] || [])];
      const [removed] = list.splice(fromIdx, 1);
      list.splice(toIdx, 0, removed);
      return {
        watchlists: {
          ...state.watchlists,
          [state.activeWatchlist]: list,
        },
      };
    }),

  setSymbolsOrder: (symbols) =>
    set((state) => ({
      watchlists: {
        ...state.watchlists,
        [state.activeWatchlist]: symbols,
      },
    })),

  createWatchlist: (name) =>
    set((state) => {
      if (state.watchlists[name]) return state;
      // Copy column config from currently active watchlist
      const sourceConfig = state.columnConfigs[state.activeWatchlist];
      return {
        watchlists: { ...state.watchlists, [name]: [] },
        columnConfigs: sourceConfig
          ? {
              ...state.columnConfigs,
              [name]: sourceConfig.map((c) => ({ ...c })),
            }
          : state.columnConfigs,
        activeWatchlist: name,
      };
    }),

  deleteWatchlist: (name) =>
    set((state) => {
      const keys = Object.keys(state.watchlists);
      if (keys.length <= 1) {
        // Last watchlist: reset to empty Favoritos with default columns
        return {
          watchlists: { Favoritos: [] },
          columnConfigs: {},
          activeWatchlist: "Favoritos",
        };
      }
      const { [name]: _, ...rest } = state.watchlists;
      const { [name]: _cc, ...restConfigs } = state.columnConfigs;
      const newActive =
        state.activeWatchlist === name
          ? Object.keys(rest)[0]
          : state.activeWatchlist;
      return {
        watchlists: rest,
        columnConfigs: restConfigs,
        activeWatchlist: newActive,
      };
    }),

  renameWatchlist: (oldName, newName) =>
    set((state) => {
      if (!state.watchlists[oldName] || state.watchlists[newName]) return state;
      const { [oldName]: symbols, ...rest } = state.watchlists;
      const { [oldName]: colConfig, ...restConfigs } = state.columnConfigs;
      return {
        watchlists: { ...rest, [newName]: symbols },
        columnConfigs: colConfig
          ? { ...restConfigs, [newName]: colConfig }
          : restConfigs,
        activeWatchlist:
          state.activeWatchlist === oldName ? newName : state.activeWatchlist,
      };
    }),

  setActiveWatchlist: (name) => set({ activeWatchlist: name }),

  setGroup: (group) => set({ group }),

  resetWatchlists: () => {
    clearAllSubscriptions();
    set({
      watchlists: { Favoritos: [] },
      columnConfigs: {},
      activeWatchlist: "Favoritos",
      group: null,
    });
  },

  // Column config actions (per-watchlist)
  setColumnVisibility: (key, visible) =>
    set((state) => {
      const active = state.activeWatchlist;
      const saved = state.columnConfigs[active] || DEFAULT_COLUMN_CONFIG;
      // Merge new columns from DEFAULT before applying visibility
      const savedKeys = new Set(saved.map((c) => c.key));
      const merged = [
        ...saved,
        ...DEFAULT_COLUMN_CONFIG.filter((c) => !savedKeys.has(c.key)),
      ];
      const config = merged.map((c) => (c.key === key ? { ...c, visible } : c));
      return { columnConfigs: { ...state.columnConfigs, [active]: config } };
    }),

  setColumnOrder: (orderedKeys) =>
    set((state) => {
      const active = state.activeWatchlist;
      const current = state.columnConfigs[active] || DEFAULT_COLUMN_CONFIG;
      const byKey = Object.fromEntries(current.map((c) => [c.key, c]));
      // Also include columns from DEFAULT (for newly added columns not yet in saved config)
      for (const col of DEFAULT_COLUMN_CONFIG) {
        if (!byKey[col.key]) byKey[col.key] = col;
      }
      const reordered = orderedKeys.map((k) => byKey[k]).filter(Boolean);
      return { columnConfigs: { ...state.columnConfigs, [active]: reordered } };
    }),

  resetColumnConfig: () =>
    set((state) => {
      const { [state.activeWatchlist]: _, ...rest } = state.columnConfigs;
      return { columnConfigs: rest };
    }),
}));

export default useWatchlistStore;
